import React from "react";
import {
	Card,
	CardContent,
	Divider,
	Grid,
	Typography,
	IconButton,
	TextField,
	MenuItem
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import FilterListIcon from "@material-ui/icons/FilterListOutlined";

import { LocalizationContext } from "../../../core/localization/Localization";
import { useFloatingOrderIndData } from "../../../data/trading/orderIndicative/hooks/useOrderIndFloatingData";
import { useNotificationHandler } from "../../../core/notifications/hooks";
import { renderOrderIndSide } from "../orderIndicative/columRenderers";
import { useAuth } from "../../../data/auth/hooks";
import { OrderSide } from "../../../data/models";
import { OrderIndFilter } from "../../../data/trading/orderIndicative/types";
import { OrderSideSelect } from "../orders/OrderSideSelect";
import { ProductSelect } from "../../components/listing/ProductSelect";

interface OrderIndFloatingViewProps {
	onClose: () => void;
}

export const OrderIndFloatingView: React.FC<OrderIndFloatingViewProps> = () => {
	const { getString } = React.useContext(LocalizationContext);

	const [showFilter, setShowFilter] = React.useState(false);
	const [popupFilter, setPopupFilter] = React.useState<OrderIndFilter>({});
	const [orderIndList, loadOrderInd] = useFloatingOrderIndData(popupFilter);

	const today = new Date();
	const [auth] = useAuth();

	const showComment =
		auth.status === "successful" &&
		auth.user.roleCode !== "MEMTRADER" &&
		auth.user.roleCode !== "MEMMANAGER" &&
		auth.user.roleCode !== "MEMCONTACT";

	React.useEffect(() => {
		loadOrderInd();
	}, [loadOrderInd]);

	useNotificationHandler<string>(
		"OrderIndChanged",
		() => {
			loadOrderInd();
		},
		[loadOrderInd]
	);

	const orderIndRows = orderIndList || [];
	const productsInOrders = React.useMemo(() => {
		const map = new Map<string, any>();

		orderIndRows
			.filter(o => o.status === 1)
			.forEach(o => {
				if (!map.has(o.productCode)) {
					map.set(o.productCode, {
						prodId: o.productCode,
						prodCode: o.productCode,
						prodName: o.productName,
					});
				}
			});

		return Array.from(map.values());
	}, [orderIndRows]);

	const data = orderIndRows.filter(o => {
		const creDat = new Date(o.creTime);

		const productOk =
			!popupFilter.product?.prodId ||
			o.productCode === popupFilter.product.prodId;

		const sideOk =
			!popupFilter.side ||
			(popupFilter.side === OrderSide.Buy && (o.side as string) === "KUPI") ||
			(popupFilter.side === OrderSide.Sell && (o.side as string) === "PRODAJ");

		return (
			o.status === 1 &&
			creDat.getFullYear() === today.getFullYear() &&
			creDat.getMonth() === today.getMonth() &&
			creDat.getDate() === today.getDate() &&
			productOk &&
			sideOk
		);
	});

	return (
		<Card
			style={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				border: "2px solid #bdbdbd",
				borderRadius: 10,
				boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
				backgroundColor: "#fff",
			}}
		>
			<Divider />

			<CardContent
				style={{
					flex: 1,
					overflowY: "auto",
					paddingTop: 8
				}}
			>
				<Grid container justify="flex-end" style={{ marginBottom: 8 }}>
					<IconButton size="small" onClick={() => setShowFilter(true)}>
						<FilterListIcon />
					</IconButton>
				</Grid>

				<Grid
					container
					spacing={1}
					style={{
						marginBottom: 8,
						fontWeight: "bold",
						borderBottom: "1px solid #ccc",
						paddingBottom: 4,
						flexWrap: "nowrap"
					}}
				>
					<Grid item style={{ width: 80 }}>
						<Typography variant="caption">{getString("orderSide")}</Typography>
					</Grid>

					<Grid item style={{ width: 140 }}>
						<Typography variant="caption">{getString("orderProduct")}</Typography>
					</Grid>

					<Grid item style={{ width: 120 }}>
						<Typography variant="caption">{getString("orderPrice")}</Typography>
					</Grid>

					<Grid item style={{ width: 120, textAlign: "right" }}>
						<Typography variant="caption">
							{getString("orderQty")}
						</Typography>
					</Grid>
					<Grid item style={{ flex: 1 }}>
						<Typography variant="caption">{getString("orderIndDescr")}</Typography>
					</Grid>

					{showComment && (
						<Grid item style={{ flex: 1 }}>
							<Typography variant="caption">{getString("orderIndComment")}</Typography>
						</Grid>
					)}
				</Grid>

				{data.map(o => (
					<Grid
						container
						key={o.orderIndId}
						spacing={1}
						style={{
							marginBottom: 6,
							borderBottom: "1px solid #f0f0f0",
							paddingBottom: 4,
							flexWrap: "nowrap",
							backgroundColor:
								(o.side as string) === "PRODAJ"
									? "#ffe5e5"
									: (o.side as string) === "KUPI"
										? "#e5f0ff"
										: "#ffffff",
							borderRadius: 6,
							padding: 6,
						}}
					>
						<Grid item style={{ width: 80 }}>
							<Typography variant="caption">
								{renderOrderIndSide(o)}
							</Typography>
						</Grid>

						<Grid item style={{ width: 140 }}>
							<Typography variant="body2">{o.productName}</Typography>
						</Grid>

						<Grid item style={{ width: 120 }}>
							<Typography variant="body2">{o.price}</Typography>
						</Grid>

						<Grid
							item
							style={{
								width: 120,
								textAlign: "right"
							}}
						>
							<Typography variant="body2">
								{o.qty} {o.unit}
							</Typography>
						</Grid>

						<Grid item style={{ flex: 1, minWidth: 0 }}>
							<Typography variant="body2">{o.description}</Typography>
						</Grid>

						{showComment && (
							<Grid item style={{ flex: 1, minWidth: 0 }}>
								<Typography variant="body2">{o.comment}</Typography>
							</Grid>
						)}
					</Grid>
				))}
			</CardContent>

			<Dialog
				open={showFilter}
				onClose={() => setShowFilter(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Filter</DialogTitle>

				<DialogContent>
					<Grid container direction="column" spacing={2}>
						<Grid item>
							<OrderSideSelect
								handleChange={e =>
									setPopupFilter({
										...popupFilter,
										side: e.target.value as OrderSide,
									})
								}
								side={popupFilter.side}
								nullable
							/>
						</Grid>

						<Grid item>
							<TextField
								select
								fullWidth
								label={getString("orderProduct")}
								value={popupFilter.product?.prodId || ""}
								onChange={e => {
									const product = productsInOrders.find(
										p => p.prodId === e.target.value
									);

									setPopupFilter({
										...popupFilter,
										product: product || undefined,
									});
								}}
							>
								<MenuItem value="">--</MenuItem>

								{productsInOrders.map(p => (
									<MenuItem key={p.prodId} value={p.prodId}>
										{p.prodName}
									</MenuItem>
								))}
							</TextField>
						</Grid>
					</Grid>
				</DialogContent>

				<DialogActions>
					<Button
						onClick={() => {
							setPopupFilter({});
						}}
					>
						Reset
					</Button>

					<Button
						variant="contained"
						color="secondary"
						onClick={() => {
							loadOrderInd();
							setShowFilter(false);
						}}
					>
						OK
					</Button>
				</DialogActions>
			</Dialog>
		</Card>
	);
};